import { ArrowLeft, Bookmark, Download, Heart, MessageCircle, Share2, Star } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import { createComment, getComments, getPost, toggleBookmark, toggleLike } from '../services/socialApi'

interface SkillCard {
  id: number
  name: string
  description: string
  category: string
  version: string
  rating: number
  downloads: number
  color?: string
  tags: string[]
  is_installed?: boolean
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<any>(null)
  const [comments, setComments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [isSaved, setIsSaved] = useState(false)
  const [shareCount, setShareCount] = useState(0)
  const [skill, setSkill] = useState<SkillCard | null>(null)

  const { user } = useAuth()
  const userId = user?.id || null

  const loadPost = React.useCallback(async () => {
    if (!id) {return}
    try {
      const data = await getPost(parseInt(id, 10), userId ? Number(userId) : undefined)
      setPost(data)
      setIsLiked(data.is_liked || false)
      setLikeCount(data.like_count || 0)
      setIsSaved(data.is_bookmarked || false)
      setShareCount(data.share_count || 0)

      // Skill discussion ise skill detayini cek
      if (data.aicloud_skill_id) {
        try {
          const token = localStorage.getItem('token') ?? ''
          const r = await fetch(`${API_URL}/api/aicloud/skills/${data.aicloud_skill_id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          })
          if (r.ok) {
            const s = await r.json() as SkillCard
            setSkill(s)
          }
        } catch { /* skill yuklenemedi, sessiz devam */ }
      }
    } catch (err) {
      console.error('Load post error:', err)
    } finally {
      setLoading(false)
    }
  }, [id, userId])

  const loadComments = React.useCallback(async () => {
    if (!id) {return}
    try {
      const data = await getComments(parseInt(id, 10))
      setComments(data)
    } catch (err) {
      console.error('Load comments error:', err)
    }
  }, [id])

  useEffect(() => {
    if (id) {
      void loadPost()
      void loadComments()
    }
  }, [id, loadPost, loadComments])

  const handleLike = async () => {
    if (!id || !userId) { navigate('/login'); return }
    const wasLiked = isLiked
    setIsLiked(!wasLiked)
    setLikeCount(wasLiked ? likeCount - 1 : likeCount + 1)
    try {
      const res = await toggleLike(Number(userId), 'post', parseInt(id, 10))
      setIsLiked(res.is_liked)
    } catch {
      setIsLiked(wasLiked)
      setLikeCount(wasLiked ? likeCount : likeCount - 1)
    }
  }

  const handleSave = async () => {
    if (!userId) { navigate('/login'); return }
    const wasSaved = isSaved
    setIsSaved(!wasSaved)
    try {
      await toggleBookmark(Number(userId), parseInt(id ?? '0', 10))
    } catch {
      setIsSaved(wasSaved)
    }
  }

  const handleShare = () => {
    if (!id) { return }
    setShareCount(shareCount + 1)
    const url = `${window.location.origin}/social/post/${id}`
    void navigator.clipboard.writeText(url)
  }

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() || !id || !userId) {return}

    setIsSubmitting(true)
    try {
      await createComment(parseInt(id, 10), { content: commentText }, Number(userId))
      setCommentText('')
      await loadComments()
      await loadPost()
    } catch (err) {
      console.error('Comment error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          Loading...
        </div>
      </MainLayout>
    )
  }

  if (!post) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          Post not found
        </div>
      </MainLayout>
    )
  }

  const authorName = post.user?.display_name || post.agent?.name || 'Unknown'
  const isAgent = !!post.agent_id

  const handleAuthorClick = () => {
    if (isAgent && post.agent_id) {
      navigate(`/agent/${post.agent_id}`)
    } else if (post.user?.id) {
      navigate(`/user/${post.user.id}`)
    }
  }

  return (
    <MainLayout>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/social')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            color: '#64748b',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f1f5f9'
            e.currentTarget.style.borderColor = '#cbd5e1'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f8fafc'
            e.currentTarget.style.borderColor = '#e2e8f0'
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Post Card */}
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div
              onClick={handleAuthorClick}
              style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: isAgent ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: '700', fontSize: '22px', flexShrink: 0,
                cursor: 'pointer'
              }}
            >
              {authorName[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span
                  onClick={handleAuthorClick}
                  style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#667eea' }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#1a202c' }}
                >{authorName}</span>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: isAgent ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                  color: isAgent ? '#3b82f6' : '#10b981'
                }}>
                  {isAgent ? '🍄 AI Agent' : '👤 Human'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#94a3b8' }}>
                {new Date(String(post.created_at)).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} •
                {new Date(String(post.created_at)).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>

          {/* Skill Karti - en uste, content'ten once */}
          {skill && (
            <div
              onClick={() => navigate(`/aicloud/app/skills/${skill.id}`)}
              style={{
                marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                background: `linear-gradient(135deg, ${skill.color ?? '#3b82f6'}12, ${skill.color ?? '#3b82f6'}06)`,
                border: `1px solid ${skill.color ?? '#3b82f6'}30`,
                display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <div style={{
                width: '42px', height: '42px', borderRadius: '10px', flexShrink: 0,
                background: `${skill.color ?? '#3b82f6'}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px',
              }}>⚡</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '3px' }}>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary, #1a202c)' }}>{skill.name}</span>
                  <span style={{
                    fontSize: '10px', padding: '1px 7px', borderRadius: '4px', fontWeight: 700,
                    background: `${skill.color ?? '#3b82f6'}20`, color: skill.color ?? '#3b82f6',
                    border: `1px solid ${skill.color ?? '#3b82f6'}40`,
                  }}>v{skill.version}</span>
                  <span style={{
                    fontSize: '10px', padding: '1px 7px', borderRadius: '4px',
                    background: 'rgba(0,0,0,.06)', color: '#64748b',
                  }}>{skill.category}</span>
                </div>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {skill.description}
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Star size={10} fill="#f59e0b" color="#f59e0b" /> {skill.rating.toFixed(1)}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Download size={10} /> {skill.downloads.toLocaleString()} installs
                  </span>
                </div>
              </div>
              <div style={{ fontSize: '16px', color: skill.color ?? '#3b82f6', flexShrink: 0 }}>→</div>
            </div>
          )}

          {/* Content */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '16px', color: '#1a202c', lineHeight: '1.8', marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
              {post.content}
            </p>

            {/* Tags */}
            {post.skill_tags && post.skill_tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {post.skill_tags.map((tag: string, idx: number) => (
                  <span key={idx} style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: '13px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: '20px',
            padding: '16px 0',
            borderTop: '1px solid #f1f5f9',
            borderBottom: '1px solid #f1f5f9',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#64748b'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={18} />
              <span>{post.like_count || 0} likes</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MessageCircle size={18} />
              <span>{post.comment_count || 0} comments</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Share2 size={18} />
              <span>{shareCount} shares</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { void handleLike() }}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '10px 20px', borderRadius: '8px',
                background: isLiked ? 'rgba(239,68,68,0.1)' : '#f8fafc',
                border: isLiked ? '1px solid #ef4444' : '1px solid #e2e8f0',
                color: isLiked ? '#ef4444' : '#64748b',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
              <span>{likeCount} Like{likeCount !== 1 ? 's' : ''}</span>
            </button>
            <button style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '10px 20px', borderRadius: '8px',
              background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
            }} onClick={handleShare}>
              <Share2 size={18} />
              <span>Share</span>
            </button>
            <button style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '10px 20px', borderRadius: '8px',
              background: isSaved ? 'rgba(245,158,11,0.1)' : '#f8fafc',
              border: isSaved ? '1px solid #f59e0b' : '1px solid #e2e8f0',
              color: isSaved ? '#f59e0b' : '#64748b',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
            }} onClick={() => { void handleSave() }}>
              <Bookmark size={18} fill={isSaved ? 'currentColor' : 'none'} />
              <span>Save</span>
            </button>
          </div>
        </div>

        {/* Comment Form - sadece login olanlar */}
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', marginBottom: '16px' }}>
            Leave a Comment
          </h3>
          {!userId ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              <p style={{ marginBottom: '12px' }}>Sign in to leave a comment.</p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Sign In
              </button>
            </div>
          ) : (
          <form onSubmit={(e) => { void handleSubmitComment(e) }}>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                color: '#1a202c',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '100px',
                marginBottom: '12px'
              }}
            />
            <button
              type="submit"
              disabled={!commentText.trim() || isSubmitting}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                background: commentText.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                border: 'none',
                color: commentText.trim() ? '#fff' : '#cbd5e1',
                fontSize: '14px',
                fontWeight: '600',
                cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                opacity: commentText.trim() ? 1 : 0.5
              }}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
          )}
        </div>

        {/* Comments List */}
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', marginBottom: '20px' }}>
            All Comments ({comments.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {comments.map((comment) => {
              const commentAuthor = comment.user?.display_name || comment.agent?.name || 'Unknown'
              const isCommentAgent = !!comment.agent_id
              return (
                <div key={comment.id} style={{
                  paddingBottom: '20px',
                  borderBottom: '1px solid #f1f5f9'
                }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: isCommentAgent ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>
                      {commentAuthor[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>{commentAuthor}</span>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: isCommentAgent ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                          color: isCommentAgent ? '#3b82f6' : '#10b981'
                        }}>
                          {isCommentAgent ? '🤖' : '👤'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                          {new Date(String(comment.created_at)).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} •
                          {new Date(String(comment.created_at)).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.7', margin: 0 }}>
                        {comment.content}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

      </div>
    </MainLayout>
  )
}
